import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

export type BrandListItem = {
  id: number;
  name: string;
  slug: string;
  queries: string[];
  sources?: string[];
  alertsFeedUrls?: string[];
  assetsCount?: number;
  maxQueries?: number;
  active: boolean;
};

type BrandCtx = {
  brands: BrandListItem[];
  activeId: number | null;
  defaultSources: string[];
  reload: () => Promise<void>;
  activate: (id: number) => Promise<void>;
};

const Ctx = createContext<BrandCtx | null>(null);

export function BrandProvider({ children }: { children: ReactNode }) {
  const [brands, setBrands] = useState<BrandListItem[]>([]);
  const [activeId, setActiveId] = useState<number | null>(null);
  const [defaultSources, setDefaultSources] = useState<string[]>([]);

  const reload = useCallback(async () => {
    try {
      const r = await fetch("/api/brands", { credentials: "same-origin" });
      if (!r.ok) return;
      const d = await r.json();
      setBrands(d.items || []);
      setActiveId(d.activeBrandId ?? null);
      if (Array.isArray(d.defaultSources)) setDefaultSources(d.defaultSources);
    } catch {
      /* basic-auth / network */
    }
  }, []);

  const activate = useCallback(
    async (id: number) => {
      await fetch(`/api/brands/${id}/activate`, { method: "POST" });
      await reload();
    },
    [reload],
  );

  useEffect(() => {
    void reload();
  }, [reload]);

  const value = useMemo(
    () => ({ brands, activeId, defaultSources, reload, activate }),
    [brands, activeId, defaultSources, reload, activate],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useBrand() {
  const v = useContext(Ctx);
  if (!v) throw new Error("BrandProvider missing");
  return v;
}

export function brandQuery(activeId: number | null): string {
  return activeId ? `?brandId=${activeId}` : "";
}
